import React, { useState, useEffect } from "react";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Toaster, toast } from "../components/ui/sonner";
import {
  LogOut, Plus, Pencil, Trash2, Download, Lock, ArrowLeft, X, Settings, Upload, ChevronUp, ChevronDown, Copy, Clock, History, Tag, Phone, RotateCcw,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  adminLogin, adminLogout, adminGoogleAuth,
  fetchAdminExperiences, createExperience, updateExperience, deleteExperience as apiDeleteExperience,
  fetchAdminBookings, exportBookingsCSV, changeAdminPassword, uploadImage, reorderExperiences,
  fetchBookingHistory, archiveBooking, archiveAllBookings, resolveImageUrl, updateBooking, restoreBooking, createManualBooking,
  deleteHistoryItems, exportHistoryCSV,
  fetchDiscountCodes, createDiscountCode, updateDiscountCode, deleteDiscountCode,
} from "../api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_AUTH_URL = "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(window.location.origin + "/admin");

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_streamlined-events/artifacts/7p0ticvb_NP%20logo%201.png";

const emptyCategory = () => ({
  name: "", price: 0, seats_total: 0, seats_remaining: 0, description: "",
});

/* ─── Experience Edit Dialog ─── */
const ExperienceEditDialog = ({ exp, isOpen, onClose, onSave }) => {
  const [form, setForm] = useState(exp || {});
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingLayout, setUploadingLayout] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [layoutProgress, setLayoutProgress] = useState(0);

  useEffect(() => {
    if (exp) setForm({ ...exp });
  }, [exp]);

  if (!exp) return null;

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleFileUpload = async (file, field, setUploading, setProgress) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const url = await uploadImage(file, (p) => setProgress(p));
      update(field, url);
      toast.success("Image uploaded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const updateCategory = (index, field, value) => {
    const cats = [...(form.categories || [])];
    cats[index] = { ...cats[index], [field]: value };
    setForm((prev) => ({ ...prev, categories: cats }));
  };

  const addCategory = () => {
    setForm((prev) => ({
      ...prev,
      categories: [...(prev.categories || []), emptyCategory()],
    }));
  };

  const removeCategory = (index) => {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border border-white/10 text-white max-w-[640px] max-h-[85vh] overflow-y-auto rounded-none">
        <DialogHeader>
          <DialogTitle className="text-white">{form.id ? "Edit" : "Add"} Experience</DialogTitle>
          <DialogDescription className="text-white/40">Fill in experience details and seat categories</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-white/60 text-sm">Title</Label>
              <Input value={form.title || ""} onChange={(e) => update("title", e.target.value)}
                className="bg-white/5 border-white/10 text-white mt-1 rounded-none" />
            </div>
            <div>
              <Label className="text-white/60 text-sm">Date</Label>
              <Input type="date" value={form.date || ""} onChange={(e) => update("date", e.target.value)}
                className="bg-white/5 border-white/10 text-white mt-1 rounded-none" />
            </div>
            <div>
              <Label className="text-white/60 text-sm">Start Time</Label>
              <Input value={form.start_time || ""} onChange={(e) => update("start_time", e.target.value)} placeholder="17:00"
                className="bg-white/5 border-white/10 text-white mt-1 rounded-none" />
            </div>
            <div>
              <Label className="text-white/60 text-sm">Duration</Label>
              <Input value={form.duration || ""} onChange={(e) => update("duration", e.target.value)} placeholder="3 hours"
                className="bg-white/5 border-white/10 text-white mt-1 rounded-none" />
            </div>
            <div>
              <Label className="text-white/60 text-sm">Venue</Label>
              <Input value={form.venue || ""} onChange={(e) => update("venue", e.target.value)}
                className="bg-white/5 border-white/10 text-white mt-1 rounded-none" />
            </div>
            <div className="col-span-2">
              <Label className="text-white/60 text-sm">City</Label>
              <Input value={form.city || ""} onChange={(e) => update("city", e.target.value)}
                className="bg-white/5 border-white/10 text-white mt-1 rounded-none" />
            </div>
            <div className="col-span-2">
              <Label className="text-white/60 text-sm">Short Hook</Label>
              <Input value={form.short_hook || ""} onChange={(e) => update("short_hook", e.target.value)}
                className="bg-white/5 border-white/10 text-white mt-1 rounded-none" />
            </div>
            <div className="col-span-2">
              <Label className="text-white/60 text-sm">Description</Label>
              <Input value={form.description || ""} onChange={(e) => update("description", e.target.value)}
                className="bg-white/5 border-white/10 text-white mt-1 rounded-none" />
            </div>
            <div className="col-span-2">
              <Label className="text-white/60 text-sm flex items-center gap-2">
                <Clock size={14} className="text-[#00FFD1]" /> Auto-Expire Date & Time
              </Label>
              <Input type="datetime-local" value={form.expires_at ? (() => { const d = new Date(form.expires_at); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })() : ""}
                onChange={(e) => update("expires_at", e.target.value ? new Date(e.target.value).toISOString() : null)}
                className="bg-white/5 border-white/10 text-white mt-1 rounded-none" />
              <p className="text-white/30 text-xs mt-1">Event will automatically become inactive after this date/time</p>
            </div>
            <div className="col-span-2">
              <Label className="text-white/60 text-sm">Experience Image</Label>
              <div className="mt-1 flex flex-col gap-2">
                {form.image_url && (
                  <div className="relative w-full h-32 border border-white/10 overflow-hidden">
                    <img src={resolveImageUrl(form.image_url)} alt="Preview" className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = "none"; }} />
                    <button onClick={() => update("image_url", "")}
                      className="absolute top-1 right-1 bg-black/70 p-1 hover:bg-red-500/80 transition-colors">
                      <X size={12} className="text-white" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-white/10 bg-white/5 text-white/60 text-sm cursor-pointer hover:border-[#00FFD1]/40 hover:text-white transition-colors">
                    <Upload size={14} />
                    {uploadingImage ? `Uploading ${imageProgress}%` : "Upload Image"}
                    <input type="file" accept="image/*" className="hidden"
                      disabled={uploadingImage}
                      onChange={(e) => handleFileUpload(e.target.files[0], "image_url", setUploadingImage, setImageProgress)} />
                  </label>
                  <span className="text-white/20 text-xs flex items-center">or</span>
                  <Input value={form.image_url || ""} onChange={(e) => update("image_url", e.target.value)}
                    placeholder="Paste URL" className="bg-white/5 border-white/10 text-white rounded-none flex-1 text-sm h-auto py-2" />
                </div>
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-white/60 text-sm">Seating Layout Image</Label>
              <div className="mt-1 flex flex-col gap-2">
                {form.seating_layout_url && (
                  <div className="relative w-full h-32 border border-white/10 overflow-hidden">
                    <img src={resolveImageUrl(form.seating_layout_url)} alt="Layout Preview" className="w-full h-full object-contain bg-black/50"
                      onError={(e) => { e.target.style.display = "none"; }} />
                    <button onClick={() => update("seating_layout_url", "")}
                      className="absolute top-1 right-1 bg-black/70 p-1 hover:bg-red-500/80 transition-colors">
                      <X size={12} className="text-white" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-white/10 bg-white/5 text-white/60 text-sm cursor-pointer hover:border-[#00FFD1]/40 hover:text-white transition-colors">
                    <Upload size={14} />
                    {uploadingLayout ? `Uploading ${layoutProgress}%` : "Upload Layout"}
                    <input type="file" accept="image/*" className="hidden"
                      disabled={uploadingLayout}
                      onChange={(e) => handleFileUpload(e.target.files[0], "seating_layout_url", setUploadingLayout, setLayoutProgress)} />
                  </label>
                  <span className="text-white/20 text-xs flex items-center">or</span>
                  <Input value={form.seating_layout_url || ""} onChange={(e) => update("seating_layout_url", e.target.value)}
                    placeholder="Paste URL" className="bg-white/5 border-white/10 text-white rounded-none flex-1 text-sm h-auto py-2" />
                </div>
              </div>
            </div>
          </div>

          {/* Payment Link */}
          <div className="border-t border-white/10 pt-4">
            <Label className="text-white/60 text-sm mb-2 block">Payment Link (Razorpay)</Label>
            <Input value={form.payment_link || ""} onChange={(e) => update("payment_link", e.target.value)}
              placeholder="https://rzp.io/..." data-testid="payment-link-input"
              className="bg-white/5 border-white/10 text-white rounded-none text-sm" />
            <p className="text-white/30 text-xs mt-1">Single payment link for this event — users will select tickets and pay on Razorpay</p>
          </div>

          {/* Categories */}
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-white/60 text-sm">Seat Categories</Label>
              <button onClick={addCategory} className="text-[#00FFD1] text-sm flex items-center gap-1 hover:underline">
                <Plus size={12} /> Add Category
              </button>
            </div>
            <div className="flex flex-col gap-4">
              {(form.categories || []).map((cat, i) => (
                <div key={i} className="border border-white/10 p-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-white text-sm font-medium">Category {i + 1}</span>
                    <button onClick={() => removeCategory(i)} className="text-white/30 hover:text-red-400 transition-colors">
                      <X size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white/40 text-xs">Name</Label>
                      <Input value={cat.name} onChange={(e) => updateCategory(i, "name", e.target.value)}
                        className="bg-white/5 border-white/10 text-white mt-1 rounded-none h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-white/40 text-xs">Price (INR)</Label>
                      <Input type="number" value={cat.price === 0 ? "" : cat.price} onChange={(e) => updateCategory(i, "price", parseInt(e.target.value) || 0)}
                        placeholder="0" onFocus={(e) => e.target.select()}
                        className="bg-white/5 border-white/10 text-white mt-1 rounded-none h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-white/40 text-xs">Total Seats</Label>
                      <Input type="number" value={cat.seats_total === 0 ? "" : cat.seats_total} onChange={(e) => updateCategory(i, "seats_total", parseInt(e.target.value) || 0)}
                        placeholder="0" onFocus={(e) => e.target.select()}
                        className="bg-white/5 border-white/10 text-white mt-1 rounded-none h-9 text-sm" />
                    </div>
                    <div>
                      <Label className="text-white/40 text-xs">Seats Left</Label>
                      <Input type="number" value={cat.seats_remaining === 0 ? "" : cat.seats_remaining} onChange={(e) => updateCategory(i, "seats_remaining", parseInt(e.target.value) || 0)}
                        placeholder="0" onFocus={(e) => e.target.select()}
                        className="bg-white/5 border-white/10 text-white mt-1 rounded-none h-9 text-sm" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-white/40 text-xs">Description</Label>
                      <Input value={cat.description || ""} onChange={(e) => updateCategory(i, "description", e.target.value)}
                        placeholder="What's included in this category..."
                        className="bg-white/5 border-white/10 text-white mt-1 rounded-none h-9 text-sm placeholder:text-white/20" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button onClick={handleSave} disabled={saving}
            className="w-full bg-[#00FFD1] text-black py-3 text-lg font-medium hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] transition-all duration-500 mt-2 disabled:opacity-50">
            {saving ? "Saving..." : "Save Experience"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Settings Panel ─── */
const SettingsPanel = () => {
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async () => {
    if (!currentPass || !newPass) {
      toast.error("Please fill all fields");
      return;
    }
    if (newPass !== confirmPass) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPass.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await changeAdminPassword(currentPass, newPass);
      toast.success("Password changed successfully!");
      setCurrentPass("");
      setNewPass("");
      setConfirmPass("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[480px]">
      <div className="flex items-center gap-3 mb-6">
        <Settings size={20} className="text-[#00FFD1]" />
        <h2 className="text-white text-xl font-semibold">Account Settings</h2>
      </div>

      <div className="border border-white/10 p-6">
        <h3 className="text-white text-lg font-medium mb-4">Change Password</h3>
        <div className="flex flex-col gap-4">
          <div>
            <Label className="text-white/60 text-sm mb-1.5 block">Current Password</Label>
            <Input type="password" value={currentPass} onChange={(e) => setCurrentPass(e.target.value)}
              placeholder="Enter current password"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 rounded-none" />
          </div>
          <div>
            <Label className="text-white/60 text-sm mb-1.5 block">New Password</Label>
            <Input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)}
              placeholder="Enter new password"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 rounded-none" />
          </div>
          <div>
            <Label className="text-white/60 text-sm mb-1.5 block">Confirm New Password</Label>
            <Input type="password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)}
              placeholder="Confirm new password"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11 rounded-none" />
          </div>
          <button onClick={handleChangePassword} disabled={saving}
            className="w-full bg-[#00FFD1] text-black py-3 text-base font-medium hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] transition-all duration-500 disabled:opacity-50 mt-2">
            {saving ? "Saving..." : "Update Password"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Discount Codes Panel ─── */
const DiscountCodesPanel = ({ discountCodes, experiences, onReload }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState({
    code: "", discount_type: "percentage", discount_value: 0,
    applies_to: "all", experience_ids: [], category_names: [], max_uses: 0, is_active: true, expires_at: "",
  });
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setForm({ code: "", discount_type: "percentage", discount_value: 0, applies_to: "all", experience_ids: [], category_names: [], max_uses: 0, is_active: true, expires_at: "" });
    setEditingCode(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error("Code is required"); return; }
    if (form.discount_value <= 0) { toast.error("Discount value must be positive"); return; }
    setSaving(true);
    const payload = { ...form, expires_at: form.expires_at || null };
    try {
      if (editingCode) {
        await updateDiscountCode(editingCode.id, payload);
        toast.success("Discount code updated");
      } else {
        await createDiscountCode(payload);
        toast.success("Discount code created");
      }
      resetForm();
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete code "${code.code}"?`)) return;
    try {
      await deleteDiscountCode(code.id);
      toast.success("Deleted");
      onReload();
    } catch { toast.error("Failed to delete"); }
  };

  const handleEdit = (code) => {
    setForm({
      code: code.code, discount_type: code.discount_type, discount_value: code.discount_value,
      applies_to: code.applies_to, experience_ids: code.experience_ids || [], category_names: code.category_names || [], max_uses: code.max_uses || 0, is_active: code.is_active,
      expires_at: code.expires_at || "",
    });
    setEditingCode(code);
    setShowForm(true);
  };

  const toggleExperienceId = (expId) => {
    setForm((prev) => ({
      ...prev,
      experience_ids: prev.experience_ids.includes(expId)
        ? prev.experience_ids.filter((id) => id !== expId)
        : [...prev.experience_ids, expId],
    }));
  };

  const toggleCategoryName = (catName) => {
    setForm((prev) => ({
      ...prev,
      category_names: prev.category_names.includes(catName)
        ? prev.category_names.filter((n) => n !== catName)
        : [...prev.category_names, catName],
    }));
  };

  // Get unique category names across all experiences
  const allCategoryNames = [...new Set(experiences.flatMap((e) => (e.categories || []).map((c) => c.name)))].sort();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <h3 className="text-white text-lg font-semibold flex items-center gap-2"><Tag size={18} /> Discount Codes</h3>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-[#00FFD1] text-black px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] transition-all duration-500">
          <Plus size={14} /> New Code
        </button>
      </div>

      {showForm && (
        <div className="border border-white/10 p-6 flex flex-col gap-4 bg-white/[0.02]">
          <div className="flex justify-between items-center">
            <h4 className="text-white font-medium">{editingCode ? "Edit Code" : "Create New Code"}</h4>
            <button onClick={resetForm} className="text-white/40 hover:text-white"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/60 text-xs">Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="SAVE20" className="bg-white/5 border-white/10 text-white rounded-none h-10 text-sm mt-1 uppercase" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Discount Type</Label>
              <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                className="w-full bg-white/5 border border-white/10 text-white h-10 text-sm mt-1 px-3">
                <option value="percentage">Percentage (%)</option>
                <option value="flat">Flat Amount (₹)</option>
              </select>
            </div>
            <div>
              <Label className="text-white/60 text-xs">Value ({form.discount_type === "percentage" ? "%" : "₹"})</Label>
              <Input type="number" value={form.discount_value === 0 ? "" : form.discount_value} onChange={(e) => setForm({ ...form, discount_value: parseInt(e.target.value) || 0 })}
                placeholder="0" onFocus={(e) => e.target.select()}
                className="bg-white/5 border-white/10 text-white rounded-none h-10 text-sm mt-1" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Max Uses (0 = unlimited)</Label>
              <Input type="number" value={form.max_uses === 0 ? "" : form.max_uses} onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value) || 0 })}
                placeholder="0 (unlimited)" onFocus={(e) => e.target.select()}
                className="bg-white/5 border-white/10 text-white rounded-none h-10 text-sm mt-1" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Applies To</Label>
              <select value={form.applies_to} onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
                className="w-full bg-white/5 border border-white/10 text-white h-10 text-sm mt-1 px-3">
                <option value="all">All Events</option>
                <option value="specific">Specific Events</option>
              </select>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="accent-[#00FFD1]" />
                <span className="text-white/60 text-sm">Active</span>
              </label>
            </div>
            <div>
              <Label className="text-white/60 text-xs">Expiry Date (optional)</Label>
              <Input type="datetime-local"
                value={form.expires_at ? (() => { const d = new Date(form.expires_at); return isNaN(d.getTime()) ? "" : `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })() : ""}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                className="bg-white/5 border-white/10 text-white rounded-none h-10 text-sm mt-1" />
              <p className="text-white/30 text-xs mt-1">Leave empty for no expiry</p>
            </div>
          </div>
          {form.applies_to === "specific" && (
            <div>
              <Label className="text-white/60 text-xs mb-2 block">Select Events</Label>
              <div className="flex flex-wrap gap-2">
                {experiences.map((exp) => (
                  <button key={exp.id} onClick={() => toggleExperienceId(exp.id)}
                    className={`px-3 py-1.5 text-xs border transition-all ${
                      form.experience_ids.includes(exp.id)
                        ? "border-[#00FFD1] text-[#00FFD1] bg-[rgba(0,255,209,0.1)]"
                        : "border-white/10 text-white/50 hover:border-white/30"
                    }`}>
                    {exp.title}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Category Filter (optional) */}
          <div>
            <Label className="text-white/60 text-xs mb-2 block">Restrict to Categories (leave empty for all)</Label>
            <div className="flex flex-wrap gap-2">
              {allCategoryNames.map((catName) => (
                <button key={catName} onClick={() => toggleCategoryName(catName)}
                  className={`px-3 py-1.5 text-xs border transition-all ${
                    form.category_names.includes(catName)
                      ? "border-[#FF8D2F] text-[#FF8D2F] bg-[rgba(255,141,47,0.1)]"
                      : "border-white/10 text-white/50 hover:border-white/30"
                  }`}>
                  {catName}
                </button>
              ))}
            </div>
            {form.category_names.length === 0 && <p className="text-white/30 text-xs mt-1">Applies to all categories</p>}
          </div>
          <button onClick={handleSave} disabled={saving}
            className="bg-[#00FFD1] text-black px-6 py-2.5 text-sm font-medium self-start hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] transition-all duration-500 disabled:opacity-50">
            {saving ? "Saving..." : editingCode ? "Update Code" : "Create Code"}
          </button>
        </div>
      )}

      {/* Codes List */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead className="text-white/40">Code</TableHead>
              <TableHead className="text-white/40">Discount</TableHead>
              <TableHead className="text-white/40">Applies To</TableHead>
              <TableHead className="text-white/40">Uses</TableHead>
              <TableHead className="text-white/40">Expiry</TableHead>
              <TableHead className="text-white/40">Status</TableHead>
              <TableHead className="text-white/40 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {discountCodes.map((code) => (
              <TableRow key={code.id} className="border-white/5">
                <TableCell className="text-white font-mono font-semibold">{code.code}</TableCell>
                <TableCell className="text-white/60">
                  {code.discount_type === "percentage" ? `${code.discount_value}%` : `₹${code.discount_value}`}
                </TableCell>
                <TableCell className="text-white/60">
                  {code.applies_to === "all" ? "All Events" : `${(code.experience_ids || []).length} event(s)`}
                  {(code.category_names || []).length > 0 && (
                    <span className="text-[#FF8D2F] text-xs ml-1">({code.category_names.join(", ")})</span>
                  )}
                </TableCell>
                <TableCell className="text-white/60">
                  {code.used_count || 0}{code.max_uses > 0 ? ` / ${code.max_uses}` : " / ∞"}
                </TableCell>
                <TableCell className="text-white/40 text-xs">
                  {code.expires_at ? (() => {
                    const d = new Date(code.expires_at);
                    const isExpired = d < new Date();
                    return <span className={isExpired ? "text-red-400" : ""}>{d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}{isExpired ? " (expired)" : ""}</span>;
                  })() : "Never"}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`rounded-none ${code.is_active ? "border-green-400/40 text-green-400" : "border-red-400/40 text-red-400"}`}>
                    {code.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => handleEdit(code)} className="p-2 text-white/40 hover:text-[#00FFD1] transition-colors"><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(code)} className="p-2 text-white/40 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {discountCodes.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-white/30 py-8">No discount codes yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

/* ─── Main Admin Page ─── */
export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [experiences, setExperiences] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingHistory, setBookingHistory] = useState([]);
  const [bookingTab, setBookingTab] = useState("active");
  const [editingBooking, setEditingBooking] = useState(null);
  const [editBookingForm, setEditBookingForm] = useState({});
  const [savingBooking, setSavingBooking] = useState(false);
  const [addingBooking, setAddingBooking] = useState(false);
  const [newBookingForm, setNewBookingForm] = useState({
    experience_id: "", name: "", phone: "", email: "", category: "", qty: 1, amount: 0, payment_status: "paid", payment_id: "", discount_code: "", discount_amount: 0,
  });
  const [selectedHistoryIds, setSelectedHistoryIds] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [editExp, setEditExp] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for Google OAuth callback - session_id comes in URL hash fragment
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
      if (sessionId) {
        handleGoogleCallback(sessionId);
        // Clean URL
        navigate("/admin", { replace: true });
        return;
      }
    }

    // Also check query params for backward compatibility
    const params = new URLSearchParams(location.search);
    const sessionIdParam = params.get("session_id");
    if (sessionIdParam) {
      handleGoogleCallback(sessionIdParam);
      navigate("/admin", { replace: true });
      return;
    }

    const token = localStorage.getItem("np_admin_token");
    if (token) {
      setIsLoggedIn(true);
      loadData();
    }
  }, []);

  const handleGoogleCallback = async (sessionId) => {
    setGoogleLoading(true);
    try {
      const result = await adminGoogleAuth(sessionId);
      setIsLoggedIn(true);
      toast.success(`Welcome, ${result.name || result.email}!`);
      loadData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Google login failed";
      toast.error(msg);
    } finally {
      setGoogleLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [exps, bks, hist, codes] = await Promise.all([
        fetchAdminExperiences(),
        fetchAdminBookings(),
        fetchBookingHistory(),
        fetchDiscountCodes(),
      ]);
      setExperiences(exps);
      setBookings(bks);
      setBookingHistory(hist);
      setDiscountCodes(codes);
    } catch (err) {
      console.error("Failed to load admin data:", err);
      if (err.response?.status === 401) {
        setIsLoggedIn(false);
        adminLogout();
      }
    }
  };

  const handleLogin = async () => {
    setLoginLoading(true);
    try {
      await adminLogin(password);
      setIsLoggedIn(true);
      toast.success("Welcome back!");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid password");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = GOOGLE_AUTH_URL;
  };

  const handleLogout = async () => {
    setIsLoggedIn(false);
    await adminLogout();
  };

  const toggleActive = async (id) => {
    const exp = experiences.find((e) => e.id === id);
    if (!exp) return;
    try {
      await updateExperience(id, { is_active: !exp.is_active });
      setExperiences(experiences.map((e) => e.id === id ? { ...e, is_active: !e.is_active } : e));
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const toggleUpcoming = async (id) => {
    const exp = experiences.find((e) => e.id === id);
    if (!exp) return;
    try {
      await updateExperience(id, { is_upcoming: !exp.is_upcoming });
      setExperiences(experiences.map((e) => e.id === id ? { ...e, is_upcoming: !e.is_upcoming } : e));
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const moveExperience = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= experiences.length) return;
    const newList = [...experiences];
    const [moved] = newList.splice(index, 1);
    newList.splice(newIndex, 0, moved);
    setExperiences(newList);
    try {
      await reorderExperiences(newList.map((e) => e.id));
    } catch (err) {
      toast.error("Failed to reorder");
      loadData();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this experience?")) return;
    try {
      await apiDeleteExperience(id);
      setExperiences(experiences.filter((e) => e.id !== id));
      toast.success("Experience deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleEditSave = async (exp) => {
    try {
      if (exp.id) {
        await updateExperience(exp.id, exp);
      } else {
        await createExperience(exp);
      }
      setIsEditOpen(false);
      setEditExp(null);
      toast.success("Experience saved");
      loadData();
    } catch (err) {
      toast.error("Failed to save experience");
    }
  };

  const handleArchiveBooking = async (bookingId) => {
    if (!window.confirm("Move this booking to history? This cannot be undone.")) return;
    try {
      await archiveBooking(bookingId);
      toast.success("Booking moved to history");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to archive booking");
    }
  };

  const handleEditBooking = (booking) => {
    setEditingBooking(booking);
    setEditBookingForm({
      name: booking.name || "",
      phone: booking.phone || "",
      email: booking.email || "",
      category: booking.category || "",
      qty: booking.qty || 1,
      amount: booking.amount || 0,
      payment_status: booking.payment_status || "pending",
      payment_id: booking.payment_id || "",
      discount_code: booking.discount_code || "",
      discount_amount: booking.discount_amount || 0,
    });
  };

  const handleSaveBooking = async () => {
    if (!editingBooking) return;
    setSavingBooking(true);
    try {
      await updateBooking(editingBooking.booking_id, editBookingForm);
      toast.success("Booking updated");
      setEditingBooking(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update booking");
    } finally {
      setSavingBooking(false);
    }
  };

  const handleRestoreBooking = async (bookingId) => {
    if (!window.confirm("Restore this booking to active?")) return;
    try {
      await restoreBooking(bookingId);
      toast.success("Booking restored to active");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore booking");
    }
  };

  const handleCreateManualBooking = async () => {
    if (!newBookingForm.name.trim() || !newBookingForm.phone.trim() || !newBookingForm.experience_id) {
      toast.error("Name, Phone and Experience are required");
      return;
    }
    setSavingBooking(true);
    try {
      await createManualBooking(newBookingForm);
      toast.success("Booking created");
      setAddingBooking(false);
      setNewBookingForm({ experience_id: "", name: "", phone: "", email: "", category: "", qty: 1, amount: 0, payment_status: "paid", payment_id: "", discount_code: "", discount_amount: 0 });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create booking");
    } finally {
      setSavingBooking(false);
    }
  };

  const handleDeleteSelectedHistory = async () => {
    if (selectedHistoryIds.length === 0) { toast.error("Select items to delete"); return; }
    if (!window.confirm(`Delete ${selectedHistoryIds.length} history entries permanently?`)) return;
    try {
      await deleteHistoryItems(selectedHistoryIds);
      toast.success(`Deleted ${selectedHistoryIds.length} entries`);
      setSelectedHistoryIds([]);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete history items");
    }
  };

  const handleExportHistoryCSV = async () => {
    try {
      await exportHistoryCSV();
      toast.success("History CSV exported");
    } catch (err) {
      toast.error("Failed to export history");
    }
  };

  const toggleHistorySelect = (bookingId) => {
    setSelectedHistoryIds((prev) =>
      prev.includes(bookingId) ? prev.filter((id) => id !== bookingId) : [...prev, bookingId]
    );
  };

  const toggleSelectAllHistory = () => {
    if (selectedHistoryIds.length === bookingHistory.length) {
      setSelectedHistoryIds([]);
    } else {
      setSelectedHistoryIds(bookingHistory.map((b) => b.booking_id));
    }
  };

  const copyBookingToClipboard = async (b) => {
    const exp = experiences.find((e) => e.id === b.experience_id);
    const text = [
      `Booking ID: ${b.booking_id}`,
      `Name: ${b.name}`,
      `Phone: ${b.phone}`,
      `Email: ${b.email}`,
      `Experience: ${exp?.title || b.experience_id}`,
      `Category: ${b.category || "-"}`,
      `Qty: ${b.qty}`,
      `Amount: ${b.amount}`,
      `Status: ${b.payment_status}`,
      `Payment ID: ${b.payment_id || "-"}`,
      `Date: ${new Date(b.created_at).toLocaleString("en-IN")}`,
    ].join("\t");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Booking copied to clipboard");
    } catch (err) {
      console.error("Clipboard error:", err);
      toast.error("Could not copy to clipboard");
    }
  };

  const getMinPrice = (cats) => cats?.length ? Math.min(...cats.map((c) => c.price)) : 0;
  const getTotalSeats = (cats) => cats?.reduce((s, c) => s + c.seats_remaining, 0) || 0;

  const handleExportCSV = async () => {
    try {
      await exportBookingsCSV();
      toast.success("CSV exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  /* Login */
  if (!isLoggedIn) {
    if (googleLoading) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-[#00FFD1]/30 border-t-[#00FFD1] rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white/60">Verifying Google account...</p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="w-full max-w-[400px]">
          <img src={LOGO_URL} alt="Network Playground" className="h-14 object-contain mb-8"
            style={{ filter: "drop-shadow(0 0 12px rgba(0,255,209,0.35))" }} />
          <div className="flex items-center gap-3 mb-6">
            <Lock size={20} className="text-[#00FFD1]" />
            <h1 className="text-white text-2xl font-semibold">Admin Access</h1>
          </div>
          <div className="flex flex-col gap-4">
            <div>
              <Label className="text-white/60 text-sm mb-1.5 block">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()} placeholder="Enter admin password"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-none" />
            </div>
            <button onClick={handleLogin} disabled={loginLoading}
              className="w-full bg-[#00FFD1] text-black py-3 text-lg font-medium hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] transition-all duration-500 disabled:opacity-50">
              {loginLoading ? "Signing in..." : "Sign In"}
            </button>
            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/30 text-sm">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <button onClick={handleGoogleLogin}
              className="w-full bg-white/10 text-white py-3 text-lg font-medium hover:bg-white hover:text-black transition-all duration-500 flex items-center justify-center gap-2">
              <svg viewBox="0 0 24 24" className="w-5 h-5">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Sign in with Google
            </button>
            <p className="text-white/20 text-xs text-center">Restricted to authorized admin email only</p>
          </div>
          <button onClick={() => navigate("/")}
            className="mt-8 flex items-center gap-2 text-white/30 hover:text-white/60 transition-colors text-sm">
            <ArrowLeft size={14} /> Back to site
          </button>
        </div>
        <Toaster position="top-center" />
      </div>
    );
  }

  /* Dashboard */
  return (
    <div className="min-h-screen bg-black">
      <header className="border-b border-white/10 px-5 md:px-[7.6923%] py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="NP" className="h-8 object-contain"
            style={{ filter: "drop-shadow(0 0 8px rgba(0,255,209,0.3))" }} />
          <h1 className="text-white text-xl font-semibold">Admin</h1>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/")} className="text-white/40 hover:text-white text-sm transition-colors">View Site</button>
          <button onClick={handleLogout} className="flex items-center gap-2 text-white/40 hover:text-red-400 text-sm transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="px-5 md:px-[7.6923%] py-8 max-w-[1400px] mx-auto">
        <Tabs defaultValue="experiences">
          <TabsList className="bg-white/5 border border-white/10 rounded-none">
            <TabsTrigger value="experiences" className="data-[state=active]:bg-[#00FFD1] data-[state=active]:text-black rounded-none">Experiences</TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[#00FFD1] data-[state=active]:text-black rounded-none">Bookings</TabsTrigger>
            <TabsTrigger value="discounts" className="data-[state=active]:bg-[#00FFD1] data-[state=active]:text-black rounded-none">Discounts</TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#00FFD1] data-[state=active]:text-black rounded-none">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="experiences" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <p className="text-white/60">{experiences.length} experiences</p>
              <button onClick={() => {
                setEditExp({ title: "", date: "", start_time: "", duration: "", venue: "", city: "",
                  short_hook: "", description: "", image_url: "", seating_layout_url: "", is_active: true,
                  payment_link: "", categories: [emptyCategory()] });
                setIsEditOpen(true);
              }} className="bg-[#00FFD1] text-black px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] transition-all duration-500">
                <Plus size={14} /> Add Experience
              </button>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-white/40 w-16">Order</TableHead>
                    <TableHead className="text-white/40">Title</TableHead>
                    <TableHead className="text-white/40">Date</TableHead>
                    <TableHead className="text-white/40">Price From</TableHead>
                    <TableHead className="text-white/40">Seats Left</TableHead>
                    <TableHead className="text-white/40">Categories</TableHead>
                    <TableHead className="text-white/40">Active</TableHead>
                    <TableHead className="text-white/40">Upcoming</TableHead>
                    <TableHead className="text-white/40">Expires</TableHead>
                    <TableHead className="text-white/40 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {experiences.map((exp, idx) => (
                    <TableRow key={exp.id} className="border-white/5">
                      <TableCell>
                        <div className="flex flex-col items-center gap-0.5">
                          <button
                            onClick={() => moveExperience(idx, -1)}
                            disabled={idx === 0}
                            className="p-0.5 text-white/30 hover:text-[#00FFD1] transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <span className="text-white/20 text-xs">{idx + 1}</span>
                          <button
                            onClick={() => moveExperience(idx, 1)}
                            disabled={idx === experiences.length - 1}
                            className="p-0.5 text-white/30 hover:text-[#00FFD1] transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="text-white font-medium">{exp.title}</TableCell>
                      <TableCell className="text-white/60">{exp.date}</TableCell>
                      <TableCell className="text-white/60">₹{getMinPrice(exp.categories)}</TableCell>
                      <TableCell className="text-white/60">{getTotalSeats(exp.categories)}</TableCell>
                      <TableCell className="text-white/60">
                        <div className="flex gap-1">
                          {exp.categories?.map((c) => (
                            <Badge key={c.name} variant="outline" className="rounded-none border-white/20 text-white/60 text-xs">{c.name}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell><Switch checked={exp.is_active} onCheckedChange={() => toggleActive(exp.id)} /></TableCell>
                      <TableCell><Switch checked={exp.is_upcoming || false} onCheckedChange={() => toggleUpcoming(exp.id)} /></TableCell>
                      <TableCell className="text-white/40 text-xs">
                        {exp.expires_at ? new Date(exp.expires_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => { setEditExp(exp); setIsEditOpen(true); }} className="p-2 text-white/40 hover:text-[#00FFD1] transition-colors">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => handleDelete(exp.id)} className="p-2 text-white/40 hover:text-red-400 transition-colors">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            {/* Sub-tabs: Active / History */}
            <div className="flex items-center gap-1 mb-6 border-b border-white/10">
              <button onClick={() => setBookingTab("active")}
                data-testid="booking-tab-active"
                className={`px-4 py-2 text-sm font-medium transition-colors ${bookingTab === "active" ? "text-[#00FFD1] border-b-2 border-[#00FFD1]" : "text-white/40 hover:text-white/60"}`}>
                Active Bookings ({bookings.length})
              </button>
              <button onClick={() => setBookingTab("history")}
                data-testid="booking-tab-history"
                className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${bookingTab === "history" ? "text-[#00FFD1] border-b-2 border-[#00FFD1]" : "text-white/40 hover:text-white/60"}`}>
                <History size={14} /> History ({bookingHistory.length})
              </button>
            </div>

            {bookingTab === "active" && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <p className="text-white/60 text-sm">{bookings.length} active bookings</p>
                  <div className="flex gap-2">
                    <button onClick={() => {
                      setNewBookingForm({ experience_id: experiences[0]?.id || "", name: "", phone: "", email: "", category: "", qty: 1, amount: 0, payment_status: "paid", payment_id: "", discount_code: "", discount_amount: 0 });
                      setAddingBooking(true);
                    }} data-testid="add-booking-btn"
                      className="bg-[#00FFD1] text-black px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] transition-all duration-500">
                      <Plus size={14} /> Add Booking
                    </button>
                    {bookings.length > 0 && (
                      <button onClick={async () => {
                        if (!window.confirm(`Archive all ${bookings.length} bookings?`)) return;
                        try { const r = await archiveAllBookings(); toast.success(r.message); loadData(); }
                        catch (e) { toast.error("Failed to archive bookings"); }
                      }} data-testid="delete-all-bookings-btn"
                        className="bg-red-500/20 text-red-400 px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-red-500 hover:text-white transition-all duration-500">
                        <Trash2 size={14} /> Delete All
                      </button>
                    )}
                    <button onClick={handleExportCSV} data-testid="export-csv-btn"
                      className="bg-white/10 text-white px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-white hover:text-black transition-all duration-500">
                      <Download size={14} /> Export CSV
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-white/40">Name</TableHead>
                        <TableHead className="text-white/40">Phone</TableHead>
                        <TableHead className="text-white/40">Experience</TableHead>
                        <TableHead className="text-white/40">Category</TableHead>
                        <TableHead className="text-white/40">Qty</TableHead>
                        <TableHead className="text-white/40">Amount</TableHead>
                        <TableHead className="text-white/40">Status</TableHead>
                        <TableHead className="text-white/40">Date</TableHead>
                        <TableHead className="text-white/40 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((b) => {
                        const exp = experiences.find((e) => e.id === b.experience_id);
                        return (
                          <TableRow key={b.booking_id} className="border-white/5">
                            <TableCell className="text-white">
                              <div>
                                <p className="font-medium">{b.name}</p>
                                <p className="text-white/40 text-xs">{b.email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-white/60 text-sm">{b.phone}</TableCell>
                            <TableCell className="text-white/60">{exp?.title || b.experience_id}</TableCell>
                            <TableCell className="text-white/60">{b.category || "-"}</TableCell>
                            <TableCell className="text-white/60">{b.qty}</TableCell>
                            <TableCell className="text-white/60">₹{b.amount?.toLocaleString("en-IN")}{b.discount_code ? <span className="text-[#23A09B] text-xs ml-1">({b.discount_code})</span> : ""}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`rounded-none ${
                                b.payment_status === "paid" ? "border-green-400/40 text-green-400" :
                                b.payment_status === "pending" ? "border-yellow-400/40 text-yellow-400" :
                                "border-red-400/40 text-red-400"
                              }`}>{b.payment_status}</Badge>
                            </TableCell>
                            <TableCell className="text-white/40 text-sm">
                              {new Date(b.created_at).toLocaleDateString("en-IN")}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <button onClick={() => handleEditBooking(b)} data-testid={`edit-booking-${b.booking_id}`}
                                  className="p-2 text-white/40 hover:text-[#00FFD1] transition-colors" title="Edit booking">
                                  <Pencil size={14} />
                                </button>
                                <button onClick={() => copyBookingToClipboard(b)} data-testid={`copy-booking-${b.booking_id}`}
                                  className="p-2 text-white/40 hover:text-[#00FFD1] transition-colors" title="Copy to clipboard">
                                  <Copy size={14} />
                                </button>
                                <button onClick={() => handleArchiveBooking(b.booking_id)} data-testid={`delete-booking-${b.booking_id}`}
                                  className="p-2 text-white/40 hover:text-red-400 transition-colors" title="Move to history">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {bookings.length === 0 && (
                        <TableRow><TableCell colSpan={9} className="text-center text-white/30 py-8">No active bookings</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}

            {bookingTab === "history" && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-white/40 text-sm">All archived bookings are stored here.</p>
                  <div className="flex gap-2">
                    {selectedHistoryIds.length > 0 && (
                      <button onClick={handleDeleteSelectedHistory} data-testid="bulk-delete-history-btn"
                        className="bg-red-500/20 text-red-400 border border-red-500/30 px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-red-500/30 transition-all">
                        <Trash2 size={14} /> Delete Selected ({selectedHistoryIds.length})
                      </button>
                    )}
                    <button onClick={handleExportHistoryCSV} data-testid="export-history-csv-btn"
                      className="text-white/60 border border-white/10 px-4 py-2 text-sm flex items-center gap-2 hover:text-white hover:border-white/30 transition-colors">
                      <Download size={14} /> Export CSV
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="w-10">
                          <input type="checkbox" className="accent-[#00FFD1]"
                            checked={bookingHistory.length > 0 && selectedHistoryIds.length === bookingHistory.length}
                            onChange={toggleSelectAllHistory} data-testid="select-all-history" />
                        </TableHead>
                        <TableHead className="text-white/40">Name</TableHead>
                        <TableHead className="text-white/40">Phone</TableHead>
                        <TableHead className="text-white/40">Experience</TableHead>
                        <TableHead className="text-white/40">Category</TableHead>
                        <TableHead className="text-white/40">Qty</TableHead>
                        <TableHead className="text-white/40">Amount</TableHead>
                        <TableHead className="text-white/40">Status</TableHead>
                        <TableHead className="text-white/40">Booked</TableHead>
                        <TableHead className="text-white/40">Archived</TableHead>
                        <TableHead className="text-white/40 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookingHistory.map((b) => {
                        const exp = experiences.find((e) => e.id === b.experience_id);
                        return (
                          <TableRow key={`hist-${b.booking_id}-${b.archived_at}`} className="border-white/5">
                            <TableCell>
                              <input type="checkbox" className="accent-[#00FFD1]"
                                checked={selectedHistoryIds.includes(b.booking_id)}
                                onChange={() => toggleHistorySelect(b.booking_id)} />
                            </TableCell>
                            <TableCell className="text-white">
                              <div>
                                <p className="font-medium">{b.name}</p>
                                <p className="text-white/40 text-xs">{b.email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-white/60 text-sm">{b.phone}</TableCell>
                            <TableCell className="text-white/60">{exp?.title || b.experience_id}</TableCell>
                            <TableCell className="text-white/60">{b.category || "-"}</TableCell>
                            <TableCell className="text-white/60">{b.qty}</TableCell>
                            <TableCell className="text-white/60">₹{b.amount?.toLocaleString("en-IN")}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`rounded-none ${
                                b.payment_status === "paid" ? "border-green-400/40 text-green-400" :
                                b.payment_status === "pending" ? "border-yellow-400/40 text-yellow-400" :
                                "border-red-400/40 text-red-400"
                              }`}>{b.payment_status}</Badge>
                            </TableCell>
                            <TableCell className="text-white/40 text-sm">
                              {new Date(b.created_at).toLocaleDateString("en-IN")}
                            </TableCell>
                            <TableCell className="text-white/40 text-sm">
                              {b.archived_at ? new Date(b.archived_at).toLocaleDateString("en-IN") : "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <button onClick={() => handleRestoreBooking(b.booking_id)} data-testid={`restore-booking-${b.booking_id}`}
                                  className="p-2 text-white/40 hover:text-[#00FFD1] transition-colors" title="Restore to active">
                                  <RotateCcw size={14} />
                                </button>
                                <button onClick={() => copyBookingToClipboard(b)} data-testid={`copy-history-${b.booking_id}`}
                                  className="p-2 text-white/40 hover:text-[#00FFD1] transition-colors" title="Copy to clipboard">
                                  <Copy size={14} />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {bookingHistory.length === 0 && (
                        <TableRow><TableCell colSpan={11} className="text-center text-white/30 py-8">No booking history yet</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </TabsContent>

          {/* Discounts Tab */}
          <TabsContent value="discounts" className="mt-6">
            <DiscountCodesPanel
              discountCodes={discountCodes}
              experiences={experiences}
              onReload={loadData}
            />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <SettingsPanel />
          </TabsContent>
        </Tabs>
      </div>

      <ExperienceEditDialog exp={editExp} isOpen={isEditOpen}
        onClose={() => { setIsEditOpen(false); setEditExp(null); }}
        onSave={handleEditSave} />

      {/* Edit Booking Dialog */}
      <Dialog open={!!editingBooking} onOpenChange={(open) => { if (!open) setEditingBooking(null); }}>
        <DialogContent className="max-w-[500px] bg-[#1a1a1a] border-white/10 text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Booking</DialogTitle>
            <DialogDescription className="text-white/40">
              {editingBooking?.booking_id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/60 text-xs">Name</Label>
                <Input value={editBookingForm.name || ""} onChange={(e) => setEditBookingForm({ ...editBookingForm, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="edit-booking-name" />
              </div>
              <div>
                <Label className="text-white/60 text-xs">Phone</Label>
                <Input value={editBookingForm.phone || ""} onChange={(e) => setEditBookingForm({ ...editBookingForm, phone: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="edit-booking-phone" />
              </div>
            </div>
            <div>
              <Label className="text-white/60 text-xs">Email</Label>
              <Input value={editBookingForm.email || ""} onChange={(e) => setEditBookingForm({ ...editBookingForm, email: e.target.value })}
                className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="edit-booking-email" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-white/60 text-xs">Category</Label>
                <Input value={editBookingForm.category || ""} onChange={(e) => setEditBookingForm({ ...editBookingForm, category: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="edit-booking-category" />
              </div>
              <div>
                <Label className="text-white/60 text-xs">Qty</Label>
                <Input type="number" min={1} value={editBookingForm.qty || 1} onChange={(e) => setEditBookingForm({ ...editBookingForm, qty: parseInt(e.target.value) || 1 })}
                  className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="edit-booking-qty" />
              </div>
              <div>
                <Label className="text-white/60 text-xs">Amount</Label>
                <Input type="number" min={0} value={editBookingForm.amount || 0} onChange={(e) => setEditBookingForm({ ...editBookingForm, amount: parseInt(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="edit-booking-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/60 text-xs">Payment Status</Label>
                <select value={editBookingForm.payment_status || "pending"}
                  onChange={(e) => setEditBookingForm({ ...editBookingForm, payment_status: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-none h-10 mt-1 px-3" data-testid="edit-booking-status">
                  <option value="pending" className="bg-black">Pending</option>
                  <option value="paid" className="bg-black">Paid</option>
                  <option value="failed" className="bg-black">Failed</option>
                  <option value="refunded" className="bg-black">Refunded</option>
                </select>
              </div>
              <div>
                <Label className="text-white/60 text-xs">Payment ID</Label>
                <Input value={editBookingForm.payment_id || ""} onChange={(e) => setEditBookingForm({ ...editBookingForm, payment_id: e.target.value })}
                  placeholder="Razorpay ID" className="bg-white/5 border-white/10 text-white placeholder:text-white/20 rounded-none h-10 mt-1" data-testid="edit-booking-payment-id" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/60 text-xs">Discount Code</Label>
                <Input value={editBookingForm.discount_code || ""} onChange={(e) => setEditBookingForm({ ...editBookingForm, discount_code: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="edit-booking-discount-code" />
              </div>
              <div>
                <Label className="text-white/60 text-xs">Discount Amount</Label>
                <Input type="number" min={0} value={editBookingForm.discount_amount || 0} onChange={(e) => setEditBookingForm({ ...editBookingForm, discount_amount: parseInt(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="edit-booking-discount-amount" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setEditingBooking(null)} className="px-4 py-2 text-white/40 hover:text-white text-sm transition-colors">Cancel</button>
              <button onClick={handleSaveBooking} disabled={savingBooking} data-testid="save-booking-btn"
                className="bg-[#00FFD1] text-black px-6 py-2 text-sm font-medium hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] transition-all duration-500 disabled:opacity-50">
                {savingBooking ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Manual Booking Dialog */}
      <Dialog open={addingBooking} onOpenChange={(open) => { if (!open) setAddingBooking(false); }}>
        <DialogContent className="max-w-[500px] bg-[#1a1a1a] border-white/10 text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="text-white">Add Booking Manually</DialogTitle>
            <DialogDescription className="text-white/40">Create a new booking directly from the admin panel</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-white/60 text-xs">Experience</Label>
              <select value={newBookingForm.experience_id}
                onChange={(e) => setNewBookingForm({ ...newBookingForm, experience_id: e.target.value, category: "" })}
                className="w-full bg-white/5 border border-white/10 text-white rounded-none h-10 mt-1 px-3" data-testid="new-booking-experience">
                {experiences.map((exp) => (
                  <option key={exp.id} value={exp.id} className="bg-black">{exp.title}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/60 text-xs">Name *</Label>
                <Input value={newBookingForm.name} onChange={(e) => setNewBookingForm({ ...newBookingForm, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="new-booking-name" />
              </div>
              <div>
                <Label className="text-white/60 text-xs">Phone *</Label>
                <Input value={newBookingForm.phone} onChange={(e) => setNewBookingForm({ ...newBookingForm, phone: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="new-booking-phone" />
              </div>
            </div>
            <div>
              <Label className="text-white/60 text-xs">Email</Label>
              <Input value={newBookingForm.email} onChange={(e) => setNewBookingForm({ ...newBookingForm, email: e.target.value })}
                className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="new-booking-email" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-white/60 text-xs">Category</Label>
                <select value={newBookingForm.category}
                  onChange={(e) => {
                    const cat = experiences.find(ex => ex.id === newBookingForm.experience_id)?.categories?.find(c => c.name === e.target.value);
                    setNewBookingForm({ ...newBookingForm, category: e.target.value, amount: cat ? cat.price * newBookingForm.qty : newBookingForm.amount });
                  }}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-none h-10 mt-1 px-3" data-testid="new-booking-category">
                  <option value="" className="bg-black">Select...</option>
                  {(experiences.find(ex => ex.id === newBookingForm.experience_id)?.categories || []).map((cat) => (
                    <option key={cat.name} value={cat.name} className="bg-black">{cat.name} - ₹{cat.price}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-white/60 text-xs">Qty</Label>
                <Input type="number" min={1} value={newBookingForm.qty} onChange={(e) => {
                  const q = parseInt(e.target.value) || 1;
                  const cat = experiences.find(ex => ex.id === newBookingForm.experience_id)?.categories?.find(c => c.name === newBookingForm.category);
                  setNewBookingForm({ ...newBookingForm, qty: q, amount: cat ? cat.price * q : newBookingForm.amount });
                }}
                  className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="new-booking-qty" />
              </div>
              <div>
                <Label className="text-white/60 text-xs">Amount</Label>
                <Input type="number" min={0} value={newBookingForm.amount} onChange={(e) => setNewBookingForm({ ...newBookingForm, amount: parseInt(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white rounded-none h-10 mt-1" data-testid="new-booking-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/60 text-xs">Payment Status</Label>
                <select value={newBookingForm.payment_status}
                  onChange={(e) => setNewBookingForm({ ...newBookingForm, payment_status: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-none h-10 mt-1 px-3" data-testid="new-booking-status">
                  <option value="paid" className="bg-black">Paid</option>
                  <option value="pending" className="bg-black">Pending</option>
                </select>
              </div>
              <div>
                <Label className="text-white/60 text-xs">Payment ID</Label>
                <Input value={newBookingForm.payment_id} onChange={(e) => setNewBookingForm({ ...newBookingForm, payment_id: e.target.value })}
                  placeholder="Optional" className="bg-white/5 border-white/10 text-white placeholder:text-white/20 rounded-none h-10 mt-1" data-testid="new-booking-payment-id" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setAddingBooking(false)} className="px-4 py-2 text-white/40 hover:text-white text-sm transition-colors">Cancel</button>
              <button onClick={handleCreateManualBooking} disabled={savingBooking} data-testid="create-booking-btn"
                className="bg-[#00FFD1] text-black px-6 py-2 text-sm font-medium hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] transition-all duration-500 disabled:opacity-50">
                {savingBooking ? "Creating..." : "Create Booking"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster position="top-center" />

    </div>
  );
}
